import React, { useEffect, useState } from "react";
import { adminFetch } from "../../api/adminApi";
import { AdminCard, AdminLabel, AdminPageHeader } from "./AdminPageHeader";
import {
  PITCH_CUSTOMER_LANG_OPTIONS,
  pitchCustomerLangLabel,
} from "../../constants/customerLanguages";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const [copiedCustomerId, setCopiedCustomerId] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    target_lang: "DA",
  });

  const load = async () => {
    const data = await adminFetch("/v1/admin/customers");
    setCustomers(data.customers || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await adminFetch("/v1/admin/customers", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm({ name: "", email: "", company: "", target_lang: "DA" });
      await load();
    } catch (e) {
      setErr(e.data?.error || e.message);
    }
  };

  const regen = async (id) => {
    if (!window.confirm("Regenerate API key? Old integrations break.")) return;
    setErr("");
    try {
      const data = await adminFetch(
        `/v1/admin/customers/${id}/regenerate-api-key`,
        { method: "POST" },
      );
      await load();
      window.alert(`New key: ${data.api_key}`);
    } catch (e) {
      setErr(e.message);
    }
  };

  const toggle = async (c) => {
    setErr("");
    try {
      await adminFetch(`/v1/admin/customers/${c.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !c.is_active }),
      });
      await load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const copyApiKey = async (c) => {
    setErr("");
    try {
      await navigator.clipboard.writeText(c.api_key);
      setCopiedCustomerId(c.id);
      window.setTimeout(() => setCopiedCustomerId(""), 1800);
    } catch (_e) {
      setErr("Could not copy API key. Select the key text or use New key to view it.");
    }
  };

  return (
    <div>
      <AdminPageHeader
        kicker="Clients"
        title="Client accounts"
        description="Manage your clients. Each client has their own API key and delivery language for their news feed."
      />

      {err && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}

      <AdminCard className="mb-10">
        <h2 className="mb-6 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
          New customer
        </h2>
        <form onSubmit={create} className="grid gap-5 md:grid-cols-2">
          <div>
            <AdminLabel>Name</AdminLabel>
            <input
              className="pitch-admin-input"
              placeholder="Acme Sports"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <AdminLabel>Email</AdminLabel>
            <input
              type="email"
              className="pitch-admin-input"
              placeholder="ops@acme.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <AdminLabel>Company (optional)</AdminLabel>
            <input
              className="pitch-admin-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
          </div>
          <div>
            <AdminLabel>Delivery language</AdminLabel>
            <select
              className="pitch-admin-input"
              value={form.target_lang}
              onChange={(e) =>
                setForm({ ...form, target_lang: e.target.value })
              }
            >
              {PITCH_CUSTOMER_LANG_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({o.value})
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Articles will be automatically translated into this language for the client's feed.
            </p>
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="pitch-admin-btn-primary">
              Create customer
            </button>
          </div>
        </form>
      </AdminCard>

      <AdminCard noPadding className="overflow-hidden">
        <div className="border-b border-white/[0.06] bg-white/[0.02] px-6 py-4">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-300">
            All clients
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.03]">
                <th className="px-4 py-3 font-mono-data text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Name
                </th>
                <th className="px-4 py-3 font-mono-data text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Lang
                </th>
                <th className="px-4 py-3 font-mono-data text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  API key
                </th>
                <th className="px-4 py-3 font-mono-data text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Active
                </th>
                <th className="px-4 py-3 text-right font-mono-data text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-white/[0.05] transition-colors hover:bg-[#CCFF00]/[0.03]"
                >
                  <td className="px-4 py-4 text-sm font-medium text-white">
                    {c.name}
                  </td>
                  <td className="px-4 py-4 font-mono-data text-xs text-slate-400">
                    {pitchCustomerLangLabel(c.target_lang)}
                  </td>
                  <td
                    className="max-w-[200px] truncate px-4 py-4 font-mono-data text-[11px] text-slate-500"
                    title={c.api_key}
                  >
                    {c.api_key}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={
                        c.is_active
                          ? "pitch-admin-badge pitch-admin-badge--writer"
                          : "pitch-admin-badge pitch-admin-badge--goalise"
                      }
                    >
                      {c.is_active ? "Live" : "Off"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        className="pitch-admin-btn-ghost !px-3 !py-2 text-[9px]"
                        onClick={() => copyApiKey(c)}
                      >
                        {copiedCustomerId === c.id ? "Copied" : "Copy key"}
                      </button>
                      <button
                        type="button"
                        className="pitch-admin-btn-ghost !px-3 !py-2 text-[9px]"
                        onClick={() => regen(c.id)}
                      >
                        New key
                      </button>
                      <button
                        type="button"
                        className="pitch-admin-btn-ghost !px-3 !py-2 text-[9px]"
                        onClick={() => toggle(c)}
                      >
                        Toggle
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
}